/* eslint-disable react/display-name */
import { useContext, useEffect } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import Error404Page from './pages/404';
import Header from './components/Header';
import Footer from './components/Footer';

import { Container } from 'react-bootstrap';
import Page from './components/Page';
import AuthContext from './store/auth-context';
import ToastContext from './store/toast-context';
import LoginPage from './pages/login';
import HomePage from './pages/index';
import RegisterPage from './pages/register';
import AccountDetailsPage from './pages/accountDetails';
import ForgotPasswordPage from './pages/forgotPassword';
import ResetPasswordPage from './pages/resetPassword';
import TaskSchedulePage from './pages/taskSchedule';
import { useRedirectIfLoggedIn } from './services/redirectIfLoggedIn';

const makePage =
  (title, Component, props = {}) =>
  () =>
    (
      <Page title={title}>
        <Component {...props} />
      </Page>
    );

const makeRedirect = (location) => () => <Redirect to={location} />;

// Usage is the same as the regular Route component, though also adds a condition that
// indicates if access to the route is permitted or not. If the condition is a function, it will be executed
// and it's return value will be used. Otherwise, it will be coerced to a boolean and evaluated. Also adds
// the deniedComponent and deniedRender props that are triggered when the page is restricted.
// deniedComponent and deniedRender props function in a manner similar to component and
// render props in the standard Route component.
const RestrictedRoute = ({
  component: Component,
  deniedComponent: DeniedComponent,
  render,
  deniedRender,
  condition,
  ...props
}) => {
  const routeAllowed = typeof condition === 'function' ? condition() : !!condition;

  return (
    <Route
      {...props}
      render={(routeProps) => {
        if (routeAllowed) {
          return Component ? <Component {...routeProps} /> : render(routeProps);
        } else {
          return DeniedComponent ? <DeniedComponent {...routeProps} /> : deniedRender(routeProps);
        }
      }}
    />
  );
};

/**
 * Creates a route component that permits navigation only if the user role passes the checks
 * applied in the `isUserAuthorized` callback function supplied by the caller.
 *
 * `isUserAuthorized` is passed an object containing role and login information from the auth
 * context of the application. This callback should return a boolean indicating whether the
 * combination of roles/login state is valid to grant access to the page. If the callback returns
 * `true` the page will load as normal. If the callback returns false, an access denied toast will
 * be displayed instead and the user will be redirected to the app home page.
 *
 * @param {({
 *  isLoggedIn: boolean,
 *  isAdmin: boolean,
 *  user: any
 * }) => boolean } isUserAuthorized
 * @returns {import('react').FunctionComponent} The resulting route component.
 */

const LoginRequiredRoute = (props) => {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const { error } = useContext(ToastContext);

  useEffect(() => {
    if (!auth.logoutNotified && !auth.isLoggedIn) {
      error('Please log in to view this page.', {
        toastId: 'pageAccessToast',
      });
    }
  });

  return (
    <RestrictedRoute
      {...props}
      condition={auth.isLoggedIn}
      deniedComponent={makeRedirect(`/?redirect=${location.pathname}`)}
    />
  );
};

const AdminRequiredRoute = (props) => {
  const auth = useContext(AuthContext);

  return (
    <RestrictedRoute
      {...props}
      condition={auth.isLoggedIn && auth.isAdmin}
      deniedComponent={makeRedirect('/home')}
    />
  );
};

const LogoutRequiredRoute = (props) => {
  useRedirectIfLoggedIn();
  return <Route {...props} />;
};

const App = () => {
  return (
    <div className="App">
      <Header />
      <Container className="main-container">
        <Switch>
          <LogoutRequiredRoute
            path="/forgot-password"
            component={makePage('Forgot Password', ForgotPasswordPage, {
              title: 'Forgot Password',
            })}
          />
          <LogoutRequiredRoute
            path="/register"
            component={makePage('Register', RegisterPage, {
              title: 'Register New Account',
            })}
          />
          <LoginRequiredRoute
            path="/account-details"
            component={makePage('Account Details', AccountDetailsPage, {
              type: 'Account Details',
            })}
          />
          <LogoutRequiredRoute
            path="/reset/:hash(.{60})"
            component={makePage('Reset Password', ResetPasswordPage, {
              type: 'Reset Password',
            })}
          />
          <AdminRequiredRoute
            path="/file-schedule"
            component={makePage('File Schedule', TaskSchedulePage)}
          />
          <LoginRequiredRoute path="/home" component={makePage('Home', HomePage)} />
          <Route
            path="/"
            component={makePage('Login', LoginPage, {
              title: 'Login',
            })}
          />
          <Route path="*" component={makePage('Page Not Found', Error404Page)} />
        </Switch>
      </Container>
      <Footer
        email="melissasm@utahcounty.gov" //TODO: Get the email from treasury department
        phone="(801) 851-8255"
        address="100 East Center Street
        Suite 1200
        Provo, Utah 84606"
        hours="8:00 am - 5:00 pm"
      />
    </div>
  );
};

export default App;
